import { queryOptions } from "@tanstack/react-query"

import api from "@/utilities/api"

import {
  ParkCheckoutError,
  ParkPassesForSaleError,
  ParkPurchasePassError,
} from "@/utilities/expectedErrors"

import {
  CHECKOUT_BASE_KEY,
  PASSES_BASE_KEY,
  PASSES_FOR_SALE_BASE_KEY,
  PURCHASE_PASS_BASE_KEY,
} from "./keys"

export const passesForSaleQueryOptions = () => {
  return queryOptions({
    queryKey: [PASSES_BASE_KEY, PASSES_FOR_SALE_BASE_KEY],
    queryFn: async () => {
      const rsp = await api.park.passesForSale.POST({})
      if ("failReason" in rsp) {
        throw new ParkPassesForSaleError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

export const passesCheckoutQueryOptions = (args: {
  rateID: string
  epoch: string
}) => {
  return queryOptions({
    queryKey: [
      PASSES_BASE_KEY,
      CHECKOUT_BASE_KEY,
      args.rateID,
      args.epoch,
      "pass",
    ],
    queryFn: async () => {
      const rsp = await api.park.checkout.POST({
        rateID: args.rateID,
        isPurchasingPass: true,
      })

      if ("failReason" in rsp) {
        throw new ParkCheckoutError(rsp.failReason)
      }

      // 1 second delay after checkout
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return rsp
    },
    retry: false,
    refetchOnMount: "always", // Always call /checkout when cart is entered
  })
}

export const confirmPassesPurchaseQueryOptions = (cognitoIDToken: string) => {
  return queryOptions({
    queryKey: [PASSES_BASE_KEY, PURCHASE_PASS_BASE_KEY, cognitoIDToken],
    queryFn: async () => {
      const rsp = await api.park.purchasePass.POST({
        cognitoIDToken,
      })

      if ("failReason" in rsp) {
        throw new ParkPurchasePassError(rsp.failReason)
      }

      return rsp
    },
    retry: (failureCount, error) => {
      // Retry up to 8 times for specific errors
      if (failureCount >= 8) return false

      // Only retry for PaymentWebhookNotCompleted and SystemError
      if (error instanceof ParkPurchasePassError) {
        return ["PaymentWebhookNotCompleted", "SystemError"].includes(
          error.failReason
        )
      }

      // Retry network errors
      return true
    },
    // First retry has no delay (skipped by TanStack Query), subsequent retries wait 1 second
    retryDelay: 1000,
  })
}
